import { extractErrorMessage, useSearchContainerLevelsQuery } from "@ots-frontend/ots-data";
import { skipToken } from "@reduxjs/toolkit/query";
import { OtsActionButton } from "../../components/OtsButton";
import { OtsDialog } from "../../components/OtsDialog";
import { OtsInlineAlert } from "../../components/OtsInlineAlert";
import { OtsTableLoader } from "../../components/OtsTableLoader";
import type { OtsTimeSeriesMarker } from "../../components/OtsTimeSeriesChart";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import { ContainerLevelChart } from "../ContainerLevelChart";

/** A tank and the stretch of time to show its readings for. */
export type ContainerLevelRequest = {
  containerId: string;
  containerName?: string;
  fromDttm: number;
  toDttm: number;
  markers?: OtsTimeSeriesMarker[];
};

export type ConnectedContainerLevelDialogProps = {
  /** Open while set. */
  request?: ContainerLevelRequest;
  onClose: () => void;
  timeZone?: string;
  volumeUnit?: string;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** A tank's readings around a ticket or tank drop, as WaterGo's tank level view shows them. */
export const ConnectedContainerLevelDialog = ({ request, onClose, timeZone, volumeUnit, brand, colorScheme }: ConnectedContainerLevelDialogProps) => {
  const { data, isFetching, error, refetch } = useSearchContainerLevelsQuery(
    request ? { containerId: request.containerId, fromDttm: request.fromDttm, toDttm: request.toDttm } : skipToken,
  );
  return (
    <OtsDialog open={!!request} onClose={onClose} title="Tank level" description={request?.containerName} size="3xl" brand={brand} colorScheme={colorScheme}>
      {error ? (
        <OtsInlineAlert tone="danger" title="The tank's readings could not be loaded"
          action={<OtsActionButton size="compact" onClick={() => void refetch()}>Try again</OtsActionButton>}>
          {extractErrorMessage(error, "Please try again.")}
        </OtsInlineAlert>
      ) : isFetching && !data ? (
        <OtsTableLoader label="Loading the tank's readings"/>
      ) : (
        <ContainerLevelChart levels={data ?? []} markers={request?.markers} timeZone={timeZone} volumeUnit={volumeUnit}/>
      )}
    </OtsDialog>
  );
};
